import { FolderPlus, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Collection } from '@/types/collection';
import type { Affiliation } from '@/types/profile';

import ModalShell from '@/components/modal-shell';
import { Button } from '@/components/ui/button';

export interface CollectionDraft {
  title: string;
  label?: string;
  affiliationIds: string[];
  governanceClass?: string;
  lifecycleClass?: string;
}

interface CollectionModalProps {
  mode: 'create' | 'edit';
  initial?: Collection | null;
  affiliations: Affiliation[];
  onClose: () => void;
  onSave: (draft: CollectionDraft) => void;
}

export default function CollectionModal({ mode, initial, affiliations, onClose, onSave }: CollectionModalProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(initial?.title ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  // New collections default to the primary affiliation (if one is set); editing keeps its own.
  const primaryId = affiliations.find((a) => a.primary)?.id;
  const [affiliationIds, setAffiliationIds] = useState<string[]>(
    initial?.affiliationIds ?? (primaryId ? [primaryId] : []),
  );
  const [governanceClass, setGovernanceClass] = useState(initial?.governanceClass ?? '');
  const [lifecycleClass, setLifecycleClass] = useState(initial?.lifecycleClass ?? '');

  function toggleAff(id: string) {
    setAffiliationIds((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));
  }

  function submit() {
    if (!title.trim()) return;
    onSave({
      title: title.trim(),
      label: label.trim() || undefined,
      affiliationIds,
      governanceClass: governanceClass || undefined,
      lifecycleClass: lifecycleClass || undefined,
    });
  }

  const selectCls = 'h-10 rounded-lg border border-ws-border bg-ws-control px-3 text-sm text-ws-text';
  const labelCls = 'text-xs font-semibold uppercase tracking-wide text-ws-muted';

  const Icon = mode === 'create' ? FolderPlus : Pencil;

  return (
    <ModalShell onClose={onClose}
      title={t(mode === 'create' ? 'collections.modal.createTitle' : 'collections.modal.editTitle')}
      icon={<Icon aria-hidden='true' className='h-5 w-5 shrink-0 text-ws-text' />}>
      <div className='flex flex-col gap-5'>
        <div className='flex flex-col gap-1.5'>
          <label className={labelCls} htmlFor='col-title'>{t('collections.modal.name')}</label>
          <input
            id='col-title'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('collections.modal.namePlaceholder')}
            className='h-10 rounded-lg border border-ws-border bg-ws-control px-3 text-sm text-ws-text placeholder:text-ws-muted'
          />
        </div>

        <div className='flex flex-col gap-1.5'>
          <label className={labelCls} htmlFor='col-label'>{t('collections.modal.type')}</label>
          <input
            id='col-label'
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t('collections.modal.typePlaceholder')}
            className='h-10 rounded-lg border border-ws-border bg-ws-control px-3 text-sm text-ws-text placeholder:text-ws-muted'
          />
        </div>

        <div className='grid grid-cols-2 gap-3'>
          <div className='flex flex-col gap-1.5'>
            <label className={labelCls} htmlFor='col-gov'>{t('collections.modal.audience')}</label>
            <select id='col-gov' value={governanceClass} onChange={(e) => setGovernanceClass(e.target.value)} className={selectCls}>
              <option value=''>{t('collections.modal.unset')}</option>
              <option value='formal'>{t('collections.governance.formal')}</option>
              <option value='informal'>{t('collections.governance.informal')}</option>
            </select>
          </div>
          <div className='flex flex-col gap-1.5'>
            <label className={labelCls} htmlFor='col-life'>{t('collections.modal.lifecycle')}</label>
            <select id='col-life' value={lifecycleClass} onChange={(e) => setLifecycleClass(e.target.value)} className={selectCls}>
              <option value=''>{t('collections.modal.unset')}</option>
              <option value='time-boxed'>{t('collections.lifecycle.timeBoxed')}</option>
              <option value='perpetual'>{t('collections.lifecycle.perpetual')}</option>
            </select>
          </div>
        </div>

        <div className='flex flex-col gap-2'>
          <span className={labelCls}>{t('collections.modal.affiliations')}</span>
          {affiliations.length === 0 ? (
            <p className='text-sm text-ws-muted'>{t('collections.modal.noAffiliations')}</p>
          ) : (
            <div className='flex flex-col gap-1.5'>
              {affiliations.map((a) => (
                <label key={a.id} className='flex cursor-pointer items-center gap-2.5 rounded-lg border border-ws-border px-3 py-2 hover:bg-ws-hover'>
                  <input type='checkbox' checked={affiliationIds.includes(a.id)} onChange={() => toggleAff(a.id)} className='accent-ws-primary' />
                  <span className='text-sm text-ws-text'>{a.institution}</span>
                  <span className='text-xs text-ws-muted'>{a.location}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className='flex items-center justify-between gap-3'>
          <Button size='lg' onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant='primary' size='lg' onClick={submit} disabled={!title.trim()}>
            {t(mode === 'create' ? 'collections.modal.create' : 'common.save')}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}
